import React, { useRef, useEffect, useState } from 'react';
import { PLAYER_CONFIG } from '../config/gameConfig';

const GameCanvas = ({ maze, playerPos, fears, drafts, portal, cellSize, levelTheme, playerDirection }) => {
  const canvasRef = useRef(null);
  const playerImageRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Load player image if configured
  useEffect(() => {
    if (PLAYER_CONFIG.imageUrl) {
      const img = new Image();
      img.onload = () => {
        playerImageRef.current = img;
        setImageLoaded(true);
        console.log('✅ Player image loaded successfully');
      };
      img.onerror = () => {
        console.warn('⚠️ Failed to load player image, using default circle');
        playerImageRef.current = null;
        setImageLoaded(false);
      };
      img.src = PLAYER_CONFIG.imageUrl;
      img.crossOrigin = 'anonymous'; // For CORS
    } else {
      setImageLoaded(false);
      playerImageRef.current = null;
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !maze) return;

    const ctx = canvas.getContext('2d');
    const width = maze[0].length * cellSize;
    const height = maze.length * cellSize;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.fillStyle = levelTheme.floorColor || '#2a2a2a';
    ctx.fillRect(0, 0, width, height);

    // Draw grid lines
    ctx.strokeStyle = 'rgba(255,255,255,0.03)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= width; i += cellSize) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, height);
      ctx.stroke();
    }
    for (let i = 0; i <= height; i += cellSize) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(width, i);
      ctx.stroke();
    }

    // Draw walls
    maze.forEach((row, y) => {
      row.forEach((cell, x) => {
        if (cell === 1) {
          const wx = x * cellSize;
          const wy = y * cellSize;
          ctx.fillStyle = levelTheme.wallColor || '#1a1a1a';
          ctx.fillRect(wx, wy, cellSize, cellSize);
          ctx.strokeStyle = 'rgba(0,0,0,0.3)';
          ctx.lineWidth = 2;
          ctx.strokeRect(wx, wy, cellSize, cellSize);
          ctx.fillStyle = 'rgba(255,255,255,0.1)';
          ctx.fillRect(wx, wy, cellSize, cellSize * 0.2);
        }
      });
    });

    const drawShadow = (x, y, r) => {
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.beginPath();
      ctx.ellipse(x, y + r * 0.8, r, r * 0.4, 0, 0, Math.PI * 2);
      ctx.fill();
    };

    // Draw Door (Replaces Portal)
    if (portal) {
      const px = portal.x * cellSize;
      const py = portal.y * cellSize;
      const size = cellSize * 0.8;

      // Door Frame
      ctx.fillStyle = '#4a4a4a';
      ctx.fillRect(px - size/2 - 4, py - size/2 - 4, size + 8, size + 8);

      // Door opening (Void/Light)
      const gradient = ctx.createLinearGradient(px, py - size/2, px, py + size/2);
      gradient.addColorStop(0, '#000000');
      gradient.addColorStop(0.5, '#1a1a1a');
      gradient.addColorStop(1, '#ffffff');
      ctx.fillStyle = gradient;
      ctx.fillRect(px - size/2, py - size/2, size, size);

      // Light Rays coming out
      ctx.save();
      ctx.globalAlpha = 0.3;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(px - size/2, py + size/2);
      ctx.lineTo(px + size/2, py + size/2);
      ctx.lineTo(px + size, py + size * 1.5);
      ctx.lineTo(px - size, py + size * 1.5);
      ctx.fill();
      ctx.restore();

      // Door Knob
      ctx.fillStyle = '#ffd700';
      ctx.beginPath();
      ctx.arc(px + size/3, py, 4, 0, Math.PI * 2);
      ctx.fill();

      // "EXIT" text above
      ctx.fillStyle = '#fff';
      ctx.font = '10px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('EXIT', px, py - size/2 - 8);
    }

    // Draw drafts
    drafts.forEach((draft, index) => {
      const sx = draft.x * cellSize;
      const sy = draft.y * cellSize;
      const pulse = Math.sin(Date.now() * 0.005 + index) * 0.2 + 1;
      const size = cellSize * 0.25;

      drawShadow(sx, sy, size);

      // Glow
      const gradient = ctx.createRadialGradient(sx, sy, 0, sx, sy, size * 2);
      gradient.addColorStop(0, levelTheme.draftColor || '#ffd700');
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(sx, sy, size * 2 * pulse, 0, Math.PI * 2);
      ctx.fill();

      // Paper icon
      ctx.save();
      ctx.translate(sx, sy + Math.sin(Date.now() * 0.003 + index) * 5);
      ctx.fillStyle = '#fef3c7';
      ctx.beginPath();
      ctx.roundRect(-size, -size * 1.2, size * 2, size * 2.4, 4);
      ctx.fill();
      ctx.fillStyle = '#d1d5db';
      ctx.fillRect(-size * 0.6, -size * 0.5, size * 1.2, 2);
      ctx.fillRect(-size * 0.6, 0, size * 1.2, 2);
      ctx.fillRect(-size * 0.6, size * 0.5, size * 0.8, 2);
      ctx.fillStyle = levelTheme.draftColor || '#d97706';
      ctx.font = `bold ${size}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('♪', 0, 0);
      ctx.restore();
    });

    // Draw fears
    fears.forEach((fear) => {
      const fx = fear.x * cellSize;
      const fy = fear.y * cellSize;
      const size = cellSize * 0.35;

      drawShadow(fx, fy, size);

      // Aura (Freeze effect if stopped)
      if (fear.isFrozen) {
        ctx.strokeStyle = '#06b6d4';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(fx, fy, size * 1.5, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Main body
      ctx.fillStyle = fear.isFrozen ? '#94a3b8' : (levelTheme.fearColor || '#ef4444');
      ctx.beginPath();
      ctx.arc(fx, fy - size * 0.2, size, Math.PI, 0);
      ctx.lineTo(fx + size, fy + size);
      for (let i = 1; i <= 3; i++) {
        ctx.quadraticCurveTo(
          fx + size - (size * 2 / 3) * (i - 0.5),
          fy + size + (i % 2 === 0 ? 5 : -5),
          fx + size - (size * 2 / 3) * i,
          fy + size
        );
      }
      ctx.lineTo(fx - size, fy - size * 0.2);
      ctx.fill();

      // Eyes
      ctx.fillStyle = 'white';
      const eyeOffset = size * 0.3;
      const eyeSize = size * 0.25;
      const angle = Math.atan2(playerPos.y * cellSize - fy, playerPos.x * cellSize - fx);
      const pupilX = Math.cos(angle) * 2;
      const pupilY = Math.sin(angle) * 2;

      ctx.beginPath();
      ctx.arc(fx - eyeOffset, fy - size * 0.2, eyeSize, 0, Math.PI * 2);
      ctx.arc(fx + eyeOffset, fy - size * 0.2, eyeSize, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = 'black';
      ctx.beginPath();
      ctx.arc(fx - eyeOffset + pupilX, fy - size * 0.2 + pupilY, eyeSize * 0.4, 0, Math.PI * 2);
      ctx.arc(fx + eyeOffset + pupilX, fy - size * 0.2 + pupilY, eyeSize * 0.4, 0, Math.PI * 2);
      ctx.fill();
    });

    // Draw Player
    const px = playerPos.x * cellSize;
    const py = playerPos.y * cellSize;
    const pSize = cellSize * 0.35;

    drawShadow(px, py, pSize);

    // Check if we should use image or default circle
    if (playerImageRef.current && imageLoaded) {
      // Draw player as image
      const imageSize = cellSize * (PLAYER_CONFIG.imageSizeMultiplier || 0.7);
      
      ctx.save();
      ctx.translate(px, py);

      // Rotate based on movement direction if enabled
      if (PLAYER_CONFIG.rotateWithMovement && playerDirection) {
        const angle = Math.atan2(playerDirection.y, playerDirection.x);
        ctx.rotate(angle);
      }

      // Draw image centered
      ctx.drawImage(
        playerImageRef.current,
        -imageSize / 2,
        -imageSize / 2,
        imageSize,
        imageSize
      );

      ctx.restore();
    } else {
      // Draw default circle player
      ctx.fillStyle = levelTheme.playerColor || '#4ade80';
      ctx.beginPath();
      ctx.arc(px, py, pSize, 0, Math.PI * 2);
      ctx.fill();

      // Shine
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.beginPath();
      ctx.arc(px - pSize * 0.3, py - pSize * 0.3, pSize * 0.25, 0, Math.PI * 2);
      ctx.fill();

      // Eyes
      const time = Date.now();
      const isBlinking = time % 3000 < 150;

      ctx.fillStyle = '#1f2937';
      if (isBlinking) {
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(px - pSize * 0.5, py - pSize * 0.1);
        ctx.lineTo(px - pSize * 0.1, py - pSize * 0.1);
        ctx.moveTo(px + pSize * 0.1, py - pSize * 0.1);
        ctx.lineTo(px + pSize * 0.5, py - pSize * 0.1);
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.arc(px - pSize * 0.3, py - pSize * 0.1, pSize * 0.15, 0, Math.PI * 2);
        ctx.arc(px + pSize * 0.3, py - pSize * 0.1, pSize * 0.15, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }, [maze, playerPos, fears, drafts, portal, cellSize, levelTheme, imageLoaded, playerDirection]);

  return (
    <div className="relative inline-block rounded-xl overflow-hidden shadow-2xl border-4 border-gray-800/50">
      <canvas ref={canvasRef} className="block bg-transparent" />
    </div>
  );
};

export default GameCanvas;